import { Image, Loader } from '@storybook';
import {
	FC,
	ReactNode,
	createRef,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';

import { BRAND_LOGO, LOGO, ROUTE_TAB_NAMES } from 'constant';
import {
	IframePropsState,
	IsBannerStatusState,
	IsCreatingFundState,
	IsOpenInIframeState,
	IsUserPermissionLoading,
	kycSettingsState,
	loginState,
} from 'global-stores';
import { envHost } from 'helpers';
import {
	INIT_CONFIGURATION_SAVE,
	useIndexedDB,
	useLogout,
	useOutsideClick,
} from 'hooks';
import {
	MOBILE_VIEW_TABS,
	PARENT_SIDEBAR,
	ROUTES,
	SIDEBAR_ACCOUNT_TABS,
	SIDEBAR_COLLAPSE_TABS,
	SIDEBAR_TABS,
	pathForTab,
} from 'routes';
import { media } from 'utils';
import { RequestDemoOpenState } from 'views/book-appointment';
import { useUserRoles } from 'views/routes-children';
import { PacketInboxfilteringTagsState } from 'views/signed-doc-packets-inbox';
import { InboxfilteringTagsState } from 'views/signed-doc-inbox';
import { useSidebarBadges } from './use-sidebar-badges';
import { AccountCard, LogoutModal, SidebarSkeletonLoading } from './components';
import { SelectedRouteState, SidebarToogelState } from './stores';
import { ToggleRoute } from './toggle-route';
import './sidebar.scss';
import './sidebar-v2.scss';

interface ISidebar {
	brandLoaded: boolean;
	children: ReactNode;
	isShowSidebar: boolean;
}

/* Rows only Simplici staff may see. Matched on TITLE, which is what the
   sidebar's existing permission gate keys on too. Deliberately NOT a
   permission: a permission can be granted to a client by mistake, and this
   must not be grantable. */
const INTERNAL_ONLY_TABS = ['Data Pulls'];

export const Sidebar: FC<ISidebar> = ({
	brandLoaded,
	children,
	isShowSidebar,
}) => {
	const ref = createRef<HTMLDivElement>();
	const [isOpen, setIsOpen] = useRecoilState(SidebarToogelState);
	const [windowSizeWidth, setWindowSizeWidth] = useState(window.innerWidth);

	// global states
	const {
		image,
		sandboxStatus,
		firstName,
		lastName,
		name,
		email,
		businessName,
		role,
	} = useRecoilValue(loginState);
	const setIsNewFund = useSetRecoilState(IsCreatingFundState);
	const {
		webAdminType,
		clientlogosmall = '',
		clientlogo = '',
	} = useRecoilValue(IframePropsState);
	const isWebComponentOpen = useRecoilValue(IsOpenInIframeState);
	const companyDetails = useRecoilValue(kycSettingsState);
	const bannerStatus = useRecoilValue(IsBannerStatusState);
	const { whitelabel } = useRecoilValue(kycSettingsState);
	const setPopupOpen = useSetRecoilState(RequestDemoOpenState);
	const setSeletedRoute = useSetRecoilState(SelectedRouteState);
	const isPermissionLoading = useRecoilValue(IsUserPermissionLoading);
	// Per-sub-route counts. The single yellow pill on the Onboarding PARENT is
	// gone: it showed one number for a group of four screens, so it could not
	// say which one needed attention. Each row now carries its own.
	const sidebarBadges = useSidebarBadges();
	const badgeMap = useMemo(
		() => ({
			/* PARENT ROLL-UPS. Shown with the group collapsed, which is how the
			   sidebar sits most of the time — a count only visible after expanding
			   is a count nobody sees. Onboarding rolls up its sessions: new in the
			   last 24h, and how many are waiting on a person. */
			Onboarding: {
				delta: sidebarBadges.sessions,
				pending: sidebarBadges.sessionsPending,
			},
			/* The parent row's title is the lowercase 'esign' (routes/constants.tsx),
			   not 'eSign' — the map is keyed by TITLE, so the casing has to match
			   exactly or the badges silently render nowhere. */
			esign: {
				delta: sidebarBadges.esign,
				pending: sidebarBadges.esignPending,
			},
			/* Same pair as esign, same meanings: how many alerts arrived in the
			   last 24h, and how many are still someone's problem. Keyed by the
			   TITLE exactly as it appears in routes/constants.tsx — 'Txn
			   Monitoring', renamed from 'Transaction Monitoring'. Get the casing
			   or the wording wrong and the badges render nowhere, silently. */
			'Txn Monitoring': {
				delta: sidebarBadges.txnMonitoring,
				pending: sidebarBadges.txnMonitoringPending,
			},

			'Onboarding Flow': {
				delta: sidebarBadges.onboardingFlow,
				deltaMore: sidebarBadges.onboardingFlowMore,
			},
			/* The sub-row carrying both: how many arrived since yesterday, and how
			   many are still waiting on a person. */
			Sessions: {
				delta: sidebarBadges.sessions,
				pending: sidebarBadges.sessionsPending,
			},
		}),
		[sidebarBadges]
	);
	const resetFilteringTagsInbox = useResetRecoilState(
		PacketInboxfilteringTagsState
	);

	const resetFilteringTagsSentbox = useResetRecoilState(
		InboxfilteringTagsState
	);

	const [openToggle, setOpenToggle] = useState('');
	// hooks
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const mediaDevice = media();
	const { logout, loading } = useLogout();
	const { deleteKey } = useIndexedDB(INIT_CONFIGURATION_SAVE);
	const { customUserTab } = useUserRoles();

	const [userProfile, setUserProfile] = useState(false);
	const [userLogout, setUserLogout] = useState(false);

	useOutsideClick(ref, () => {
		setUserProfile(false);
	});

	// this will change the tabs
	const handleChangeTab = useCallback(
		(tabName: string) => {
			setSeletedRoute(tabName);
			/* Request Demo is a modal, not a destination — intercepted here for the
			   same reason Logout is, now that it lives in the main menu where every
			   row calls handleChangeTab(ROUTE). */
			if (tabName === 'Request Demo') {
				setPopupOpen(true);
				return;
			}
			if (tabName === 'Logout') {
				setUserLogout(true);
				deleteKey('session');
			} else if (tabName === 'User Profile') {
				setUserProfile(pre => !pre);
			} else {
				if (ROUTE_TAB_NAMES.includes(tabName)) {
					resetFilteringTagsInbox();
					resetFilteringTagsSentbox();
				}
				/* Resolved through pathForTab, not `/${tabName}`. A tab name is a
				   PERMISSION KEY that usually happens to equal its path segment;
				   Transaction Monitoring's three do not, and are nested besides, so
				   every one of its rows navigated to a path that does not exist and
				   was redirected to /dashboard. See TAB_PATH in routes/constants. */
				navigate(pathForTab(tabName));
			}
			setIsNewFund(false);
		},
		// eslint-disable-next-line
		[navigate]
	);

	/**
	 * INTERNAL-ONLY ROWS, hidden from anyone without a Simplici address.
	 *
	 * A SECOND, STRICTER GATE — the permission check below decides who among
	 * STAFF sees a row; this decides whether the viewer is staff at all. Kept
	 * separate rather than folded into a permission because a permission can be
	 * granted to a client by mistake, and this must not be grantable.
	 *
	 * AND IT IS NOT THE ACCESS CONTROL. Hiding a row leaves the URL and the API
	 * behind it untouched, so the real refusal is middleware on every
	 * /data-pulls endpoint. This only keeps the row out of the way of people it
	 * would confuse.
	 */
	const internalOnlyAllowed = useCallback(
		(tabName: string) => {
			if (!INTERNAL_ONLY_TABS.includes(tabName)) return true;
			return String(email ?? '')
				.trim()
				.toLowerCase()
				.endsWith('@satschel.com');
		},
		[email]
	);

	// this will manage the rolewise visibility of tabs
	const conditionalActiveTab = useCallback(
		(tabName: string) => {
			/* NOT EVERY ROW IS A PERMISSIONED SURFACE.
			   This gate admits only names present in PARENT_SIDEBAR, which is
			   derived from the user's granted tabs — correct for Sessions or
			   Billing, wrong for Request Demo, which is a contact CTA everyone
			   can use and maps to no permission key. That is why it used to live
			   in the ungated Account group; moving it to the main menu without
			   this exemption rendered nothing at all. */
			if (tabName === 'Request Demo') return true;

			const Tabs: Record<string, string | string[]> =
				windowSizeWidth > 1024
					? isOpen
						? PARENT_SIDEBAR
						: MOBILE_VIEW_TABS
					: MOBILE_VIEW_TABS;
			/* A KEY MAY UNLOCK MORE THAN ONE ROW. eVault's two pages share the one
			   `evault` permission, so MOBILE_VIEW_TABS maps it to a LIST of labels
			   (see the note on that map). `concat` flattens both shapes — a plain
			   string stays a one-element list — so every single-row key resolves to
			   exactly what it did before. The own-property guard matters because
			   `customUserTab` is server data: `constructor` would otherwise reach
			   the prototype. */
			const filteredValues = Array.from(
				new Set(
					(customUserTab ?? []).flatMap((key: string) =>
						Object.prototype.hasOwnProperty.call(Tabs, key)
							? ([] as string[]).concat(Tabs[key] ?? [])
							: []
					)
				)
			);
			const isAllow = filteredValues?.some(el => el === tabName);

			return isAllow ?? false;
		},
		[windowSizeWidth, customUserTab, isOpen]
	);
	// this will manage the visibility of integrate tab ie: will visible only if the plan is paid
	// as we have remove the payment for now there fore we are not using the function
	const isShowIntegrateTab = useCallback(
		(tabName: string) => {
			if (tabName === 'Integrate' || tabName === 'Api Docs') {
				return !isWebComponentOpen;
			}
			return true;
		},
		[isWebComponentOpen]
	);

	useEffect(() => {
		const handleWindowResize = () => {
			setWindowSizeWidth(window.innerWidth);
		};
		window.addEventListener('resize', handleWindowResize);
		return () => {
			window.removeEventListener('resize', handleWindowResize);
		};
	});

	/**
	 * A group opens itself when you are inside it.
	 *
	 * `openToggle` used to start empty and only ever be set by a click, so
	 * landing on /sessions showed a collapsed "Onboarding" with no hint that
	 * Sessions lives under it — the design shows the active group expanded with
	 * its children, which is also how you tell where you are.
	 */
	useEffect(() => {
		const owner = SIDEBAR_TABS.tabs.find(
			({ ROUTE, SUB_ROUTE }: any) =>
				pathname === `/${ROUTE}` ||
				pathname.includes(`/${ROUTE}`) ||
				(SUB_ROUTE ?? []).some(
					(sub: any) =>
						pathname === `/${sub.ROUTE}` || pathname.includes(`/${sub.ROUTE}`)
				)
		);
		if (owner?.SUB_ROUTE?.length) setOpenToggle(owner.TITLE);
	}, [pathname]);

	// here tabs are rendered using map
	const mapRender = useMemo(() => {
		const isLiveRoute = (ROUTE: any, SUB_ROUTE: any) => {
			let isExist = false;
			if (pathname === `/${ROUTE}` || pathname.includes(`/${ROUTE}`)) {
				isExist = true;
			} else {
				SUB_ROUTE?.map((route: any) => {
					if (
						pathname === `/${route.ROUTE}` ||
						pathname.includes(`/${route.ROUTE}`)
					) {
						isExist = true;
					}
				});
			}
			return isExist;
		};
		const lists = isOpen ? SIDEBAR_TABS : SIDEBAR_COLLAPSE_TABS;
		const excludedRoutes = [
			'Captable',
			'Stake holder',
			'Share class',
			'Equidity plan',
		];
		const tabs = lists.tabs.filter(
			item => !excludedRoutes.some(title => title === item.TITLE)
		);
		const listingData = envHost === 'stage' ? lists.tabs : tabs;
		return (
			<div className="anything">
				{isOpen && <div className="sidebar__group">{lists.label}</div>}
				{listingData.map(
					({ ICON, ROUTE = '', TAB_INDEX, TITLE, SUB_ROUTE, PILL }: any, index: number) =>
						conditionalActiveTab(TITLE) &&
						internalOnlyAllowed(TITLE) &&
						isShowIntegrateTab(TITLE) && (
							/* KEYED ON TITLE, NOT TAB_INDEX ALONE. TAB_INDEX is not unique
							   within SIDEBAR_COLLAPSE_TABS — "eVault dashboard" and
							   "Transfers" are both 50, "Draft" and "Inbox" both 99 — and this
							   wrapper is the element React reconciles. With duplicate keys the
							   collapsed → expanded swap (any viewport ≤ 1024px, e.g. docking
							   DevTools, then widening and re-opening the rail) left the
							   duplicates orphaned in the DOM as empty `--center` rows: a
							   blank 32px gap per orphan under the groups above them. */
							<div key={`${TAB_INDEX}-${TITLE}`}>
								<div
									className={`sidebar__item ${
										isLiveRoute(ROUTE, SUB_ROUTE)
											? 'sidebar__item--active'
											: ''
									} sidebar__item${!isOpen ? '--center' : ''}`}
									onClick={() => {
										if (!(SUB_ROUTE && isOpen && windowSizeWidth > 1024)) {
											handleChangeTab(ROUTE);
										}
									}}
									key={`${TAB_INDEX}-${TITLE}`}
								>
									{!(SUB_ROUTE && isOpen && windowSizeWidth > 1024) && (
										<i
											className={`sidebar__icon ${(isLiveRoute(ROUTE, SUB_ROUTE)?`${ICON}fill`:`${ICON}line`)} 
											${
												(!isOpen && sandboxStatus && ROUTE === 'integrate') || 
												(ROUTE === 'integrate' && windowSizeWidth <= 1024)
													? 'd-none'
													: ''
											}`}
											id={ICON}
										/>
									)}
									{SUB_ROUTE && isOpen && windowSizeWidth > 1024 ? (
										<ToggleRoute
											index={index}
											openToggle={openToggle}
											setOpenToggle={setOpenToggle}
											route={{ ICON, ROUTE, TAB_INDEX, TITLE, SUB_ROUTE }}
											handleChangeTab={handleChangeTab}
											pathname={pathname}
											setisActive={isLiveRoute(ROUTE, SUB_ROUTE)}
											badges={badgeMap}
										/>
									) : (
										<div className={`sidebar__text  ${!isOpen && 'd-none'} `}>
											{TITLE}
											{/* Marks a staff-only surface so nobody mistakes it for a
											    tenant feature. It is a LABEL, not the control — see
											    `internalOnlyAllowed` and the endpoint middleware. */}
											{PILL && <span className="sidebar__pill">{PILL}</span>}
										</div>
									)}
								</div>
								{!isOpen && (
									<ReactTooltip
										anchorId={ICON}
										place="right"
										content={TITLE}
										style={{ zIndex: 1000, color: '#ffffff' }}
									/>
								)}
							</div>
						)
				)}
			</div>
		);
	}, [
		isOpen,
		pathname,
		conditionalActiveTab,
		internalOnlyAllowed,
		isShowIntegrateTab,
		windowSizeWidth,
		openToggle,
		handleChangeTab,
		sandboxStatus,
		/* badgeMap WAS MISSING, AND THAT IS WHY THE NUMBERS NEVER APPEARED.
		   This memo builds the entire menu, `<ToggleRoute badges={badgeMap} />`
		   included. Without badgeMap in the deps the JSX is frozen at the first
		   render — when every count is still null, because nothing has been
		   fetched yet — and no later state change rebuilds it. The counts landed
		   correctly in the hook and were handed to a menu that had already been
		   memoised without them, so RowBadges kept receiving {delta: null,
		   pending: null} and returned null.

		   That is why the badges appeared only after some UNRELATED change
		   (a route change, a recoil update) happened to invalidate this memo on
		   another dependency — which reads as "sometimes they show, sometimes
		   they don't", and on a fresh login as "esign never has numbers". */
		badgeMap,
	]);
	useEffect(() => {
		if (mediaDevice !== 'large_screens' && isOpen) {
			setIsOpen(false);
		}
		// eslint-disable-next-line
	}, [mediaDevice]);

	const showUserProfileImage = useMemo(() => {
		return {
			...(image && { url: image }),
			...(!image && { fileName: 'AvatarDefault-Light.svg' }),
			className: 'user_avatar',
		};
	}, [image]);
	// this will render the bottom alligned  at bottom
	const mapProfileTabs = useMemo(
		() => (
			<div className={`${isOpen ? 'bottom-nav' : 'bottom_collapse_tabs'}`}>

				<div className="bottom-nav__nav-items">
					{SIDEBAR_ACCOUNT_TABS.tabs.map(
						({ ICON, TAB_INDEX, TITLE, isWebcomponentShow }) => {
							if (!isWebcomponentShow && isWebComponentOpen) {
								return;
							}
							return (
								<div
									className={`sidebar__item sidebar__item${
										!isOpen ? '--center' : ''
									}`}
									onClick={() => {
										if (TAB_INDEX === 3) handleChangeTab('User Profile');
										if (TAB_INDEX === 4) setPopupOpen(true);
										if (TAB_INDEX === 5) handleChangeTab('Logout');
									}}
									key={`${TAB_INDEX}-${TITLE}`}
								>
									{TITLE === 'User Profile' ? (
										<div
											id="user-profile-image"
											className="user-profile-image-wrapper"
										>
											<Image
												{...showUserProfileImage}
												className={
													isOpen
														? 'sidebar--user-profile-image'
														: 'sidebar--user-profile-image__close'
												}
											/>
											{!isOpen && (
												<ReactTooltip
													anchorId="user-profile-image"
													place="right"
													content={TITLE}
													style={{ zIndex: 1000, color: '#ffffff' }}
												/>
											)}
										</div>
									) : (
										<i className={`sidebar__icon ${ICON}`} id={ICON} />
									)}
									{!isOpen && (
										<ReactTooltip
											anchorId={ICON}
											place="right"
											content={TITLE}
											style={{ zIndex: 1000, color: '#ffffff' }}
										/>
									)}
									<div className={`sidebar__text ${!isOpen && 'd-none'} `}>
										{TITLE}
									</div>
								</div>
							);
						}
					)}
				</div>
			</div>
		),
		[
			handleChangeTab,
			isOpen,
			setPopupOpen,
			showUserProfileImage,
			isWebComponentOpen,
		]
	);

	// this will manage the click event on the logo
	const handleLogoClick = useCallback(
		(path: string) => {
			navigate(path);
		},
		[navigate]
	);

	const handleOpen = useCallback(() => {
		setIsOpen(pre => !pre);
		// eslint-disable-next-line
	}, []);

	const webComponentImage = useMemo(() => {
		return (
			<img
				src={
					(!isOpen ? clientlogosmall : clientlogo) ||
					companyDetails.logo ||
					BRAND_LOGO
				}
				alt="logo"
				className="sidebar-container__companyLogo"
			/>
		);
	}, [clientlogo, clientlogosmall, companyDetails.logo, isOpen]);

	const logoUrl = useMemo(() => {
		if (clientlogo?.length || clientlogosmall?.length) {
			return !isOpen ? clientlogosmall : clientlogo;
		} else return !isOpen ? BRAND_LOGO : LOGO.LIGHT;
	}, [clientlogo, clientlogosmall, isOpen]);

	const renderBrandLogo = useMemo(() => {
		if (!brandLoaded) {
			return <Loader type="loader" dimension={20} className="loader-blue" />;
		}

		if (webAdminType === 'onboarded' || isWebComponentOpen || whitelabel) {
			return webComponentImage;
		} else {
			return (
				<img
					src={logoUrl}
					alt="companylogo"
					className="sidebar-container__brandLogo__img"
				/>
			);
		}
	}, [
		brandLoaded,
		isWebComponentOpen,
		logoUrl,
		webAdminType,
		webComponentImage,
		whitelabel,
	]);

	return (
		<div className="Sidebar--container__main">
			{isShowSidebar && (
				<div
					className={`sidebar-container ${
						isOpen ? 'open-sidebar' : 'collapse-sidebar'
					}`}
					style={{ width: isOpen ? '242px' : '72px' }}
				>
					<div
						onClick={() => handleLogoClick(ROUTES.DASHBOARD)}
						className="sidebar-container__brandLogo"
					>
						{renderBrandLogo}
					</div>
					<div className="side-Container">
						<div className="outside-sidebar">
							<div className="logo-details" onClick={handleOpen}>
								<i
									className={`${
										isOpen
											? 'ri-arrow-left-s-line left-accor'
											: 'ri-arrow-right-s-line right-accor'
									}`}
								/>
							</div>
							<div
								className={`sidebar-tabs-container sidebar-tabs-container${
									!bannerStatus ? '--banner-active' : ''
								}`}
							>
								{/* THE ONLY SCROLLER. The nav is the designated elastic
								    element: it takes all the spare room and it is the only
								    thing that gives room back, so nothing below it can ever
								    be pushed out of view. */}
								<div className="sidebar sidebar--nav">
									{isPermissionLoading ? <SidebarSkeletonLoading /> : mapRender}
								</div>
								{webAdminType !== 'onboarded' && (
									<div className="sidebar sidebar--fixed">{mapProfileTabs}</div>
								)}

								{/* THE ACCOUNT FOOTER.
									"Who am I" and "manage who I am" are the same thought, so
									the identity block IS the User Profile control — it used to
									be inert text with a separate nav row pointing at it. Log
									out moves in beneath it and stops impersonating navigation:
									it is quieter at rest and previews its consequence in red
									before the click, which is what a destructive control owes
									you. Sandbox becomes a chip; `live` prints nothing, because
									the absence is the signal and announcing the normal case
									makes the dangerous one less visible. */}
								{webAdminType !== 'onboarded' &&
									(isOpen ? (
										<div className="sidebar__account" ref={ref}>
											{/* Opens IN FLOW rather than as a floating panel: no
											    z-index fight with ReactModal or react-flow, and no
											    offsets to re-measure the next time the rail changes
											    width — which is exactly how the old popover ended up
											    18px underneath it. */}
											{userProfile && <AccountCard />}

											<button
												type="button"
												className="sidebar__account-id"
												title={`${email} — edit your profile`}
												aria-expanded={userProfile}
												onClick={() => handleChangeTab('User Profile')}
											>
												{/* The avatar is back. The sv2 rail is text-only for
												    NAVIGATION — an icon column beside every route is
												    what made it read as a different product — but this
												    row is a person, not a route, and a face is the
												    fastest way to confirm which account you are in. */}
												<Image
													{...showUserProfileImage}
													className="sidebar__account-avatar"
												/>
												<span className="sidebar__account-text">
													<span className="sidebar__account-name">
														<b>
															{[firstName, lastName]
																.filter(Boolean)
																.join(' ') ||
																name ||
																'Signed in'}
														</b>
														{role && (
															<span className="sidebar__account-role">
																{role}
															</span>
														)}
													</span>
													<span className="sidebar__account-mail">{email}</span>
													{(businessName || sandboxStatus) && (
														<span className="sidebar__account-org">
															{businessName && (
																<span className="sidebar__account-biz">
																	{businessName}
																</span>
															)}
															{sandboxStatus && (
																<i className="sidebar__account-mode">
																	Sandbox
																</i>
															)}
														</span>
													)}
												</span>
												{/* NAMED, NOT A CHEVRON. A bare `›` says "something
												    happens" and leaves a reviewer hunting for where
												    profile editing went — which is exactly what
												    happened. The word says it. */}
												{/* The verb tracks the state — a control still saying
												    "Edit" while the thing it opened is open is lying. */}
												<span className="sidebar__account-go">
													{userProfile ? 'Close' : 'Edit'}
												</span>
											</button>

											<button
												type="button"
												className="sidebar__account-logout"
												onClick={() => handleChangeTab('Logout')}
											>
												Log out
											</button>
										</div>
									) : (
										/* Collapsed: initials, not an image — the sv2 rail is
										   text-only. Log out is deliberately absent: a
										   destructive action must never be an unlabelled
										   target. Collapsing used to drop the identity
										   entirely; the monogram keeps it, and keeps SANDBOX
										   visible, which is the one state here that has cost
										   people real data. */
										<button
											type="button"
											className={`sidebar__account-mono${
												sandboxStatus ? ' sidebar__account-mono--sandbox' : ''
											}`}
											title={`${
												[firstName, lastName].filter(Boolean).join(' ') ||
												name ||
												'Account'
											}${sandboxStatus ? ' — SANDBOX' : ''}`}
											onClick={() => {
												/* A 72px rail cannot show an account, so asking for it
												   OPENS the rail with the card already expanded — one
												   gesture, one surface, instead of a flyout floating
												   over whatever page is behind. */
												setIsOpen(true);
												if (!userProfile) handleChangeTab('User Profile');
											}}
										>
											{image ? (
												<Image
													{...showUserProfileImage}
													className="sidebar__account-avatar"
												/>
											) : (
												((firstName?.[0] ?? '') + (lastName?.[0] ?? '')).toUpperCase() ||
												'ME'
											)}
										</button>
									))}

								{userLogout && (
									<div className="user-logout-modal">
										<LogoutModal
											isOpen={userLogout}
											handleButton={logout}
											setLogout={setUserLogout}
											loading={loading}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}
			<div className="Sidebar--children">{children}</div>
		</div>
	);
};
